import { useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import * as Avatar from '@givebutter/primitives/avatar';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { pluralize } from '@givebutter/util';
import { faDownload, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteCampaignTeam, fetchCampaignTeams } from 'api/campaigns';
import { generateExports } from 'api/contacts/exports';
import * as ExportDataDialog from 'assets/js/components/ExportDataDialog';
import { CampaignTeamsSplash } from 'assets/js/pages/campaign-settings/campaign-teams/CampaignTeamSplash';
import { RaisedTotal } from 'assets/js/pages/campaigns/RaisedTotal';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { UnstyledAnchor } from 'components/unstyled-link';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { Team } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<Team>();

const CampaignTeams = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const history = useHistory();
    const match = useRouteMatch();
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign?.id!;
    const [search, setSearch] = usePersistentSearch();
    const debouncedSearch = useDebounce(search);

    const { pagination, setPagination } = Table.useReactTableState();

    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
            query: debouncedSearch,
        },
        500,
    );

    const { data: teams } = useSuspenseQuery({
        queryKey: campaignQueryKeys.teamsFiltered(campaignId, params),
        queryFn: () => fetchCampaignTeams(campaignId, params),
    });

    const { mutateAsync: exportTeams } = useMutation({
        mutationFn: () =>
            generateExports(accountId, {
                scope: 'all',
                object: 'campaign',
                report_type: 'csv',
                resource: 'teams',
                object_id: campaignId,
            }),
        onError: () => {
            toast.error('Unable to generate report');
        },
    });

    const { mutate: deleteTeam } = useMutation({
        mutationFn: (teamId: number) => deleteCampaignTeam(campaignId, teamId),
        onSuccess: () => {
            toast.success('Deleted team successfully');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.teams(campaignId),
                type: 'all',
            });
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Failed to delete team');
        },
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                header: 'Team',
                cell: info => {
                    const team = info.row.original;
                    return (
                        <div className={'flex items-center gap-2'}>
                            <Avatar.Root size="xs">
                                <Avatar.Image
                                    alt={`Profile picture of ${team.name}`}
                                    src={team.logo}
                                />
                                <Avatar.Fallback aria-label={team.name}>
                                    {team.name[0]}
                                </Avatar.Fallback>
                            </Avatar.Root>
                            <p className={'font-medium'}>{team.name}</p>
                        </div>
                    );
                },
            }),
            columnHelper.accessor('raised', {
                header: 'Raised',
                cell: info => {
                    const team = info.row.original;

                    return <RaisedTotal className={'w-full'} model={team} />;
                },
                meta: {
                    className: '!h-20',
                },
            }),
            columnHelper.accessor('members', {
                header: 'Members',
                cell: info => {
                    const memberCount = info.getValue();

                    return (
                        <div>
                            {memberCount} {pluralize(memberCount, 'member')}
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const team = info.row.original;

                    return (
                        <DropdownMenu.Root key={team.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content sideOffset={5} align={'end'}>
                                <UnstyledAnchor
                                    target={'_blank'}
                                    rel="noopener noreferrer"
                                    href={team.url}
                                >
                                    <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                        View page
                                    </DropdownMenu.Item>
                                </UnstyledAnchor>
                                <DropdownMenu.Item
                                    onClick={() => history.push(`${match.url}/${team.id}`)}
                                    onSelect={e => e.preventDefault()}
                                >
                                    Edit
                                </DropdownMenu.Item>
                                <AlertDialog.Root type={'error'}>
                                    <AlertDialog.Trigger type={'reset'} asChild={true}>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            Delete
                                        </DropdownMenu.Item>
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Portal>
                                        <AlertDialog.Overlay />
                                        <AlertDialog.Content>
                                            <AlertDialog.Header>
                                                <AlertDialog.Icon />
                                                <AlertDialog.Close />
                                            </AlertDialog.Header>
                                            <AlertDialog.Body>
                                                <AlertDialog.Title>Delete team</AlertDialog.Title>
                                                <AlertDialog.Description>
                                                    You will not be able to undo this action.
                                                </AlertDialog.Description>
                                            </AlertDialog.Body>
                                            <AlertDialog.Footer>
                                                <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                                                <AlertDialog.Action
                                                    onClick={() => deleteTeam(team.id)}
                                                >
                                                    Delete
                                                </AlertDialog.Action>
                                            </AlertDialog.Footer>
                                        </AlertDialog.Content>
                                    </AlertDialog.Portal>
                                </AlertDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [deleteTeam, history, match.url],
    );

    const table = useReactTable({
        data: teams?.data ?? [],
        columns,
        pageCount: teams?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
    });

    useSyncPaginatedData(table, teams);

    if (!campaign?.enableMultiTeam?.active) {
        return <CampaignTeamsSplash />;
    }

    return (
        <div className={'flex flex-col gap-4'}>
            <p className={'text-2xl font-medium'}>Teams</p>
            <div className={'flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap'}>
                <Input.Root>
                    <Input.SearchIcon />
                    <Input.Form
                        value={search}
                        placeholder={'Search'}
                        onChange={e => setSearch(e.target.value)}
                    />
                </Input.Root>
                <div className={'flex flex-wrap gap-2 sm:flex-nowrap'}>
                    <ExportDataDialog.Root label="Teams" onSubmit={exportTeams}>
                        <ExportDataDialog.Trigger asChild={true}>
                            <Button variant={'outline'} color={'neutral'}>
                                <FontAwesomeIcon icon={faDownload} />
                                <p>Export</p>
                            </Button>
                        </ExportDataDialog.Trigger>
                    </ExportDataDialog.Root>
                    <Button variant="solid" onClick={() => history.push(`${match.url}/new`)}>
                        <FontAwesomeIcon icon={faPlus} />
                        <p>Add team</p>
                    </Button>
                </div>
            </div>
            <Table.Root isLoading={isPending} variant={'bordered'} table={table}>
                <div className={'flex flex-col gap-y-4'}>
                    <Box className={'overflow-x-auto'}>
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant={'secondary'}>
                            <Table.EmptyIcon />
                            <Table.EmptyTitle>No teams found</Table.EmptyTitle>
                        </Table.Empty>
                    </Box>
                    <Table.Pagination>
                        <Table.PaginationPageNavigation />
                    </Table.Pagination>
                </div>
            </Table.Root>
        </div>
    );
};

export { CampaignTeams };
